import { useEffect, useState } from 'react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, GripVertical } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface SectionVisibility {
  id: string;
  sectionId: string;
  isVisible: boolean;
  order?: number;
  updatedAt: string;
}

interface SectionItem {
  id: string;
  label: string;
  order: number;
}

const defaultSections: SectionItem[] = [
  { id: 'hero', label: 'المطلع', order: 0 },
  { id: 'programs', label: 'البرامج', order: 1 },
  { id: 'statistics', label: 'الإحصائيات', order: 2 },
  { id: 'profile', label: 'الملف التعريفي', order: 3 },
  { id: 'news', label: 'الأخبار', order: 4 },
  { id: 'articles', label: 'المقالات', order: 5 },
  { id: 'partners', label: 'الشركاء', order: 6 },
  { id: 'contact', label: 'التواصل', order: 7 }
];

export function VisibilitySection() {
  const [sectionVisibility, setSectionVisibility] = useState<Record<string, boolean>>({});
  const [sections, setSections] = useState<SectionItem[]>(defaultSections);
  const [loading, setLoading] = useState(true);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchSectionVisibility();
  }, []);

  const fetchSectionVisibility = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'landing_section_visibility'));

      const visibilityMap: Record<string, boolean> = {};
      const orderMap: Record<string, number> = {};
      
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        visibilityMap[data.sectionId] = data.isVisible;
        if (data.order !== undefined) {
          orderMap[data.sectionId] = data.order;
        }
      });

      // Sort sections by order if available
      const sortedSections = [...defaultSections].map(section => ({
        ...section,
        order: orderMap[section.id] !== undefined ? orderMap[section.id] : section.order
      })).sort((a, b) => a.order - b.order);

      setSections(sortedSections);
      setSectionVisibility(visibilityMap);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching section visibility:', error);
      toast.error('حدث خطأ أثناء تحميل إعدادات الرؤية');
      setLoading(false);
    }
  };

  const toggleSectionVisibility = async (sectionId: string) => {
    try {
      const newVisibility = !sectionVisibility[sectionId];
      const section = sections.find(s => s.id === sectionId);

      await setDoc(doc(db, 'landing_section_visibility', sectionId), {
        sectionId,
        isVisible: newVisibility,
        order: section?.order || 0,
        updatedAt: new Date().toISOString()
      });

      setSectionVisibility(prev => ({
        ...prev,
        [sectionId]: newVisibility
      }));

      toast.success('تم تحديث حالة الرؤية بنجاح');
    } catch (error) {
      console.error('Error updating section visibility:', error);
      toast.error('حدث خطأ أثناء تحديث حالة الرؤية');
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === index) return;

    const newSections = [...sections];
    const draggedSection = newSections[draggedIndex];
    
    // Remove dragged item and insert at new position
    newSections.splice(draggedIndex, 1);
    newSections.splice(index, 0, draggedSection);
    
    // Update order values
    const updatedSections = newSections.map((section, idx) => ({
      ...section,
      order: idx
    }));
    
    setSections(updatedSections);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;

    try {
      // Save new order to Firebase
      const updatePromises = sections.map((section) =>
        setDoc(doc(db, 'landing_section_visibility', section.id), {
          sectionId: section.id,
          isVisible: sectionVisibility[section.id] ?? true,
          order: section.order,
          updatedAt: new Date().toISOString()
        })
      );

      await Promise.all(updatePromises);
      toast.success('تم حفظ ترتيب الأقسام بنجاح');
    } catch (error) {
      console.error('Error saving section order:', error);
      toast.error('حدث خطأ أثناء حفظ الترتيب');
    }

    setDraggedIndex(null);
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-xl font-bold">إدارة رؤية وترتيب الأقسام</h3>
        <p className="text-sm text-muted-foreground mt-1">
          اسحب الأقسام لإعادة ترتيبها • انقر على أيقونة العين لإظهار/إخفاء القسم
        </p>
      </div>
      <div className="space-y-3">
        {sections.map((section, index) => (
          <div
            key={section.id}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            className={`flex items-center justify-between p-4 bg-secondary/50 rounded-lg hover:bg-secondary/70 transition-all cursor-move ${
              draggedIndex === index ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
            }`}
          >
            <div className="flex items-center gap-3">
              <GripVertical className="h-5 w-5 text-muted-foreground" />
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-muted-foreground bg-secondary px-2 py-1 rounded">
                  {index + 1}
                </span>
                <span className="text-base font-medium">{section.label}</span>
              </div>
            </div>
            <button
              onClick={() => toggleSectionVisibility(section.id)}
              className={`p-2 rounded-full transition-colors ${
                sectionVisibility[section.id]
                  ? 'bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
              }`}
            >
              {sectionVisibility[section.id] ? (
                <Eye className="h-5 w-5" />
              ) : (
                <EyeOff className="h-5 w-5" />
              )}
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

